// ガチャ演出動画の先読み。
//
// 動画のダウンロードは購入APIのレスポンス後にしか始まらないため、回線が細いと
// そのぶんユーザーを待たせる。抽選結果は事前に分からないが、バックエンドの
// 出現率(product/effects.py)上 normal_1 が突出して出やすいので、購入前に
// これだけ先読みしておけば大多数のガチャで待ち時間がなくなる。
//
// 状態はモジュールレベルで持つ(React のツリー外)。購入成功時に PlayBefore は
// アンマウントされるが、それは先読みがまさに役に立つ瞬間なので、コンポーネントに
// fetch を所有させると落とした分を捨ててしまう。

// 動画の差し替え時はここを上げる。public/ 配下はCRAがハッシュを付けずそのまま
// コピーするためURLが固定で、パスを変えないと古いファイルがキャッシュされ続ける。
const VIDEO_BASE_PATH = `${process.env.PUBLIC_URL || ''}/video/v2`;

// 動画名が未指定の場合のフォールバック
export const FALLBACK_VIDEO_NAME = 'normal_1';

// 最も出現率が高く、先読みの対象にする動画
export const PRELOAD_VIDEO_NAME = 'normal_1';

// 演出動画のURLを組み立てる唯一の入口。
// 先読みと <video> が別々にURLを組むと、1文字の差異で同じ動画を二重に
// ダウンロードすることになる(開発環境では速いので気づけない)。
export const effectVideoUrl = (name: string): string =>
  `${VIDEO_BASE_PATH}/${name || FALLBACK_VIDEO_NAME}.mp4`;

type NetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

const getConnection = (): NetworkInformation | undefined =>
  (navigator as unknown as { connection?: NetworkInformation }).connection;

// データセーバーON時は GachaEffectVideo が動画自体を再生しないので、先読みしても
// 捨てるだけになる。2G相当の回線では投機的な通信そのものが害になる。
export const shouldPreloadEffectVideo = (): boolean => {
  const conn = getConnection();
  if (!conn) return true;
  if (conn.saveData) return false;
  return conn.effectiveType !== '2g' && conn.effectiveType !== 'slow-2g';
};

type PreloadRecord = {
  name: string;
  objectUrl: string | null;
  promise: Promise<string | null>;
};

let record: PreloadRecord | null = null;

// 先読みを開始する。冪等・投げっぱなしで、例外は投げない。
export const preloadEffectVideo = (name: string): void => {
  if (!name || !shouldPreloadEffectVideo()) return;
  if (record && record.name === name) return;

  if (record?.objectUrl) {
    URL.revokeObjectURL(record.objectUrl);
  }

  const current: PreloadRecord = { name, objectUrl: null, promise: Promise.resolve(null) };
  current.promise = fetch(effectVideoUrl(name))
    .then((res) => (res.ok ? res.blob() : null))
    .then((blob) => {
      // 待っている間に別の動画へ差し替わっていたら破棄する
      if (!blob || record !== current) return null;
      current.objectUrl = URL.createObjectURL(blob);
      return current.objectUrl;
    })
    .catch(() => null);

  record = current;
};

// 先読み済みなら再生に使えるURLを返す(同期)。未取得なら null。
export const getPreloadedEffectVideo = (name: string): string | null =>
  record && record.name === name ? record.objectUrl : null;

// 同じ動画の先読みが進行中なら、その完了を待つ Promise を返す。
// 進行中に <video> をネットワークURLで描画すると同一ファイルを二重に取得して
// 実効帯域が半分になるため、呼び出し側はこれを待つこと。
export const whenEffectVideoPreloaded = (name: string): Promise<string | null> | null =>
  record && record.name === name && !record.objectUrl ? record.promise : null;
